/**
 * Google Tag Manager, loaded from JavaScript rather than from the HTML.
 *
 * WHY NOT THE HTML SNIPPET GTM GIVES YOU
 * --------------------------------------
 * This repo has TWO html entry points — `public/index.html` (the live CRA
 * build) and `index.html` at the root (the Vite path). A snippet pasted into
 * one of them silently stops existing the day the build path changes, and
 * nothing fails loudly when analytics quietly goes missing. Loading from a
 * module means one implementation, covered by tests, for both.
 *
 * The `<noscript>` iframe half of GTM's snippet is deliberately NOT
 * reproduced: this is a React SPA, so a browser with JavaScript disabled
 * renders nothing to measure in the first place.
 *
 * CONSENT (decision of 30 Sep 2026)
 * ---------------------------------
 * Consent Mode v2, defaults DENIED, set before the container loads — the
 * order matters, because a default that arrives after GTM has initialised is
 * a default that never applied. GA4 then runs cookieless until the visitor
 * accepts, and `grantAnalyticsConsent()` promotes it.
 */

const CONTAINER_ID = process.env.REACT_APP_GTM_ID || '';

// GTM ids are `GTM-` plus 6–8 upper-case alphanumerics. Validated rather than
// merely truthiness-checked so a placeholder left in an env file ("changeme",
// "TODO") cannot inject a script tag pointing at nothing.
const VALID_ID = /^GTM-[A-Z0-9]{6,10}$/;

let loaded = false;

export function isConfigured() {
  return VALID_ID.test(CONTAINER_ID);
}

export function pushToDataLayer(payload) {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(payload);
}

/** gtag's own shape: it pushes `arguments`, not an object. */
function gtag() {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  // eslint-disable-next-line prefer-rest-params
  window.dataLayer.push(arguments);
}

/**
 * Denied by default, for everything that can identify someone. Storage that
 * is structurally necessary stays granted — those two are not tracking, and
 * denying them breaks the container rather than protecting anyone.
 */
export function setConsentDefaults() {
  gtag('consent', 'default', {
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
    analytics_storage: 'denied',
    functionality_storage: 'granted',
    security_storage: 'granted',
    // Hold tags briefly so a visitor who has already accepted is measured on
    // the page they landed on, rather than from their second page onward.
    wait_for_update: 500,
  });
}

export function grantAnalyticsConsent() {
  gtag('consent', 'update', {
    analytics_storage: 'granted',
    ad_storage: 'granted',
    ad_user_data: 'granted',
    ad_personalization: 'granted',
  });
}

export function denyAnalyticsConsent() {
  gtag('consent', 'update', {
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_user_data: 'denied',
    ad_personalization: 'denied',
  });
}

/**
 * Idempotent. Without the guard, React 18's StrictMode double-invoke in
 * development injects the container twice and every event is counted twice.
 */
export function loadGtm() {
  if (loaded || !isConfigured() || typeof document === 'undefined') return false;
  loaded = true;

  setConsentDefaults();
  pushToDataLayer({ 'gtm.start': Date.now(), event: 'gtm.js' });

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(CONTAINER_ID)}`;
  document.head.appendChild(script);
  return true;
}

/** Tests only — the module-level `loaded` latch would otherwise leak between them. */
export function __resetForTests() {
  loaded = false;
}
